import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api, { createDecoder } from "../../api";
import { createErrorReportingAsyncThunk } from "../../helpers";
import {
  DriverRoutesResponse,
  driverRoutesResponseT,
  driverRoutesResponseWithVersionT,
  RouteCargoStopNew,
} from "./types";
import {
  pauseCargoTimeTracking,
  resumeCargoTimeTracking,
  startCargoTimeTracking,
  stopCargoTimeTracking,
} from "../../app/cargo-time-tracking";
import { logoutSession } from "../../auth";
import { moveUndispatchedCargoToRoute } from "../undispatched-cargos";
import { ERR_ROUTES_STOPS_DATA_CORRUPTION } from "dora-shared";
import { RootState } from "../../../redux-store";
import { arrived, departed, stopCompleted } from "../../app/driver-activities";
import { DateTime } from "luxon";

type State = {
  initialLoaded: boolean;
  requestState: "idle" | "loading" | "success" | "error";
  errorReason: typeof ERR_ROUTES_STOPS_DATA_CORRUPTION | null;
  data: DriverRoutesResponse | null;
  showCompletedDispatcherCreatedStops: boolean;
  selectedTrailerId: string | null;
  expandedRow: ExpandedRow | null;
  version: number | null;
  receivedDataValid: boolean;
};

export type ExpandedRow = {
  id: string;
  type: "driverCreatedOrder" | "dispatcherCreatedStop";
};

const initialState: State = {
  initialLoaded: false,
  receivedDataValid: true,
  requestState: "idle",
  errorReason: null,
  data: null,
  version: null,
  showCompletedDispatcherCreatedStops: false,
  selectedTrailerId: null,
  expandedRow: null,
};

const prefix = "data/route-with-stops";

export const loadRouteWithStops = createAsyncThunk(
  `${prefix}/load-route-with-stops`,
  async (routeId: string, thunkAPI) => {
    const data = await api(thunkAPI).get({
      url: `/drivers-api/routes/${routeId}`,
      decoder: createDecoder(driverRoutesResponseT),
      noChangeConnectionStateOnError: true,
    });

    let isDataValid = true;
    for (const cargo of data.cargos) {
      const cargoStops = cargo.stops.map(
        (cargoStopId) =>
          data.routeStops.find(
            (rs) => rs.type === "CARGO_STOP" && rs.stop.id === cargoStopId
          ) as RouteCargoStopNew
      );

      for (const rs of cargoStops) {
        if (!rs) {
          isDataValid = false;
        }
      }
    }

    thunkAPI.dispatch(setReceivedDataValidAndComplete(isDataValid));

    return data;
  }
);

export const loadRouteWithStopsUsingVersion = createAsyncThunk(
  `${prefix}/load-route-with-stops-using-version`,
  async (routeId: string, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const version = state.data.routeWithStops.version;
    const data = await api(thunkAPI).get({
      url: `/drivers-api/routes/with-version/${routeId}${version ? `?version=${version}` : ""}`,
      decoder: createDecoder(driverRoutesResponseWithVersionT),
      noChangeConnectionStateOnError: true,
    });

    if (data.hasChanged) {
      let isDataValid = true;
      for (const cargo of data.data!.cargos) {
        const cargoStops = cargo.stops.map(
          (cargoStopId) =>
            data.data!.routeStops.find(
              (rs) => rs.type === "CARGO_STOP" && rs.stop.id === cargoStopId
            ) as RouteCargoStopNew
        );

        for (const rs of cargoStops) {
          if (!rs) {
            isDataValid = false;
          }
        }
      }

      thunkAPI.dispatch(setReceivedDataValidAndComplete(isDataValid));
    }

    return data;
  }
);

export const deleteStop = createErrorReportingAsyncThunk(
  `${prefix}/delete-stop`,
  async (
    input: { routeId: string; stopId: string; cargoId: string },
    thunkAPI
  ) => {
    const { routeId, cargoId, stopId } = input;
    return api(thunkAPI).delete({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}`,
      decoder: createDecoder(driverRoutesResponseWithVersionT),
    });
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    setSelectedTrailerId: (state, action) => {
      state.selectedTrailerId = action.payload;
    },
    setShowCompletedDispatcherCreatedStops: (state, action) => {
      state.showCompletedDispatcherCreatedStops = action.payload;
    },
    expandRow: (state, action) => {
      state.expandedRow = action.payload;
    },
    collapseRow: (state) => {
      state.expandedRow = null;
    },
    setReceivedDataValidAndComplete: (state, action) => {
      state.receivedDataValid = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(stopCompleted.fulfilled, (state, action) => {
      const { stopId, dateTime } = action.meta.arg;
      state.data!.routeStops = state.data!.routeStops.map((s) => {
        if (s.type === "CARGO_STOP" && s.stop.id === stopId) {
          return {
            ...s,
            stop: {
              ...s.stop,
              completedAt: (dateTime || DateTime.utc()).toISO(),
            },
          };
        }
        return s;
      });
    });
    builder.addCase(arrived.fulfilled, (state, action) => {
      const { stopId, dateTime } = action.meta.arg;
      const index = state.data!.routeStops.findIndex(
        (s) => s.type === "CARGO_STOP" && s.stop.id === stopId
      );
      if (index !== -1 && "stop" in state.data!.routeStops[index]) {
        state.data!.routeStops[index].stop.arrivedAt = (
          dateTime || DateTime.utc()
        ).toISO();
      }
    });
    builder.addCase(departed.fulfilled, (state, action) => {
      const { stopId, dateTime } = action.meta.arg;
      const index = state.data!.routeStops.findIndex(
        (s) => s.type === "CARGO_STOP" && s.stop.id === stopId
      );
      if (index !== -1 && "stop" in state.data!.routeStops[index]) {
        state.data!.routeStops[index].stop.departedAt = (
          dateTime || DateTime.utc()
        ).toISO();
      }
    });
    builder.addCase(startCargoTimeTracking.fulfilled, (state, action) => {
      const cargo = state.data?.cargos.find(
        (c) => c.id === action.meta.arg.cargoId
      );
      if (cargo) {
        if (cargo.timeTracking) {
          cargo.timeTracking.started = action.meta.arg.data.time.toISO();
        } else {
          cargo.timeTracking = {
            started: action.meta.arg.data.time.toISO(),
            ended: null,
            pauseResume: [],
          };
        }
      }
    });
    builder.addCase(stopCargoTimeTracking.fulfilled, (state, action) => {
      const cargo = state.data?.cargos.find(
        (c) => c.id === action.meta.arg.cargoId
      );
      if (cargo?.timeTracking) {
        cargo.timeTracking.ended = action.meta.arg.data.time.toISO();
      }
    });
    builder.addCase(pauseCargoTimeTracking.fulfilled, (state, action) => {
      const cargo = state.data?.cargos.find(
        (c) => c.id === action.meta.arg.cargoId
      );
      if (cargo?.timeTracking) {
        cargo.timeTracking.pauseResume.push({
          type: "PAUSE",
          time: action.meta.arg.data.time.toISO(),
        });
      }
    });
    builder.addCase(resumeCargoTimeTracking.fulfilled, (state, action) => {
      const cargo = state.data?.cargos.find(
        (c) => c.id === action.meta.arg.cargoId
      );
      if (cargo?.timeTracking) {
        cargo.timeTracking.pauseResume.push({
          type: "RESUME",
          time: action.meta.arg.data.time.toISO(),
        });
      }
    });
    builder.addCase(loadRouteWithStops.pending, (state) => {
      state.requestState = "loading";
    });
    builder.addCase(loadRouteWithStops.fulfilled, (state, action) => {
      state.requestState = "success";
      state.errorReason = null;
      state.data = action.payload;
      state.initialLoaded = true;
    });
    builder.addCase(deleteStop.fulfilled, (state, action) => {
      state.requestState = "success";
      state.errorReason = null;
      if (action.payload.hasChanged) {
        state.data = action.payload.data;
      }
      state.version = action.payload.version;
      state.initialLoaded = true;
    });
    builder.addCase(loadRouteWithStops.rejected, (state, action) => {
      if (action.error.message === ERR_ROUTES_STOPS_DATA_CORRUPTION) {
        state.errorReason = ERR_ROUTES_STOPS_DATA_CORRUPTION;
        state.initialLoaded = true;
      }
      state.requestState = "error";
      // state.data = null;
    });
    builder.addCase(loadRouteWithStopsUsingVersion.pending, (state) => {
      state.requestState = "loading";
    });
    builder.addCase(
      loadRouteWithStopsUsingVersion.fulfilled,
      (state, action) => {
        state.requestState = "success";
        state.errorReason = null;
        if (action.payload.hasChanged) {
          state.data = action.payload.data;
        }
        state.version = action.payload.version;
        state.initialLoaded = true;
      }
    );
    builder.addCase(
      loadRouteWithStopsUsingVersion.rejected,
      (state, action) => {
        if (action.error.message === ERR_ROUTES_STOPS_DATA_CORRUPTION) {
          state.errorReason = ERR_ROUTES_STOPS_DATA_CORRUPTION;
          state.initialLoaded = true;
        }
        state.requestState = "error";
      }
    );
    builder.addCase(logoutSession.fulfilled, (state) => {
      state.requestState = initialState.requestState;
      state.data = initialState.data;
      state.showCompletedDispatcherCreatedStops =
        initialState.showCompletedDispatcherCreatedStops;
      state.expandedRow = initialState.expandedRow;
      state.version = null;
    });
    builder.addCase(moveUndispatchedCargoToRoute.fulfilled, (state, action) => {
      const routeId = action.meta.arg.routeId;
      if (!routeId && state.data) {
        const cargoId = action.meta.arg.cargoId;
        state.data.cargos = state.data.cargos.filter((c) => c.id !== cargoId);
        state.data.routeStops = state.data.routeStops.filter(
          (rs) => rs.type === "WAYPOINT" || rs.stop.cargoId !== cargoId
        );
      }
    });
  },
});

export default slice.reducer;

export const {
  setShowCompletedDispatcherCreatedStops,
  setSelectedTrailerId,
  expandRow,
  collapseRow,
  setReceivedDataValidAndComplete,
} = slice.actions;
